import { SubmitButton } from "@xola/ui-kit";
import { isEqual } from "lodash";
import React, { useRef } from "react";
import { Permission } from "types";
import { useStripeTerminal } from "../../../hooks/useStripeTerminal";
import { getAutoPrint, getDefaultPrinter } from "../../../storages/PrinterStorage";
import { IsGranted } from "../../Common/IsGranted";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ContentLayout } from "../../Layout/ContentLayout";
import { PaymentTerminalForm, PaymentTerminalFormRef } from "./PaymentTerminalForm";
import { ReceiptPrintingForm, ReceiptPrintingFormRef } from "./ReceiptPrintingForm";
import { TicketPrintingForm, TicketPrintingFormRef } from "./TicketPrintingForm";

export const HardwareSettings = () => {
    /** We want to initialize the stripe terminal when the hardware settings page is loaded */
    useStripeTerminal();

    const paymentTerminalFormRef = useRef<PaymentTerminalFormRef>(null);
    const receiptPrintingFormRef = useRef<ReceiptPrintingFormRef>(null);
    const ticketPrintingFormRef = useRef<TicketPrintingFormRef>(null);

    const handleSubmit = () => {
        // Check for PaymentTerminalForm
        if (!isEqual(paymentTerminalFormRef.current?.initialValues, paymentTerminalFormRef.current?.values)) {
            paymentTerminalFormRef.current?.handleSubmit();
        }

        // Check for ReceiptPrintingForm
        // Instead of comparing with ref here is comparing with local storage
        // Because ReceiptPrintingForm doesn`t triggers any rerenders since it`s ref
        // It happens because there is no any requests. Only local storage updating
        if (
            getDefaultPrinter() !== receiptPrintingFormRef.current?.values?.defaultPrinter ||
            getAutoPrint() !== receiptPrintingFormRef.current?.values?.autoPrint
        ) {
            receiptPrintingFormRef.current?.handleSubmit();
        }

        // Check for ReceiptPrintingForm
        if (!isEqual(ticketPrintingFormRef.current?.initialValues, ticketPrintingFormRef.current?.values)) {
            ticketPrintingFormRef.current?.handleSubmit();
        }
    };

    const handleReset = () => {
        // Reset all forms
        paymentTerminalFormRef.current?.handleReset();
        receiptPrintingFormRef.current?.handleReset();
        ticketPrintingFormRef.current?.handleReset();
    };

    return (
        <ContentLayout isWide>
            <h2 className="mt-4">Hardware</h2>
            <IsGranted permissions={Permission.PAYMENT_TERMINAL_VIEW}>
                <ContentLayout.Section title="Payment Terminal">
                    <PaymentTerminalForm ref={paymentTerminalFormRef} />
                </ContentLayout.Section>
            </IsGranted>

            <IsGranted permissions={Permission.RECEIPT_PRINTING_VIEW}>
                <ContentLayout.Section title="Receipt Printing">
                    <ReceiptPrintingForm ref={receiptPrintingFormRef} />
                </ContentLayout.Section>
            </IsGranted>

            <IsGranted permissions={Permission.TICKET_PRINTING_VIEW}>
                <ContentLayout.Section title="Ticket Printing">
                    <TicketPrintingForm ref={ticketPrintingFormRef} />
                </ContentLayout.Section>
            </IsGranted>
            <ContentLayout.Footer preferenceSidebar>
                <SubmitButton
                    isLoading={
                        ticketPrintingFormRef.current?.isSubmitting ??
                        receiptPrintingFormRef.current?.isSubmitting ??
                        paymentTerminalFormRef.current?.isSubmitting
                    }
                    onClick={handleSubmit}
                >
                    Save
                </SubmitButton>

                <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};
